import { ChangeDetectorRef, Component, HostBinding, Input, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { UserClaims, AuthService } from '@anecdotes/auth-core';
import { delayedPromise, WindowHelperService } from '@anecdotes/utils';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CommentingFacadeService,
  CommentPanelManagerService,
  ConfirmationModalService,
} from '../../services';
import { CommentInputValue, UserModel } from '@anecdotes/commenting';
import { SavableCommentInputComponent } from '../savable-comment-input/savable-comment-input.component';
import { commentDraftLocalStoragePrefix } from '../../constants';
import { CommentDraft } from '../../models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-reply',
  templateUrl: './reply.component.html',
})
export class ReplyComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('input', { static: true })
  private input: SavableCommentInputComponent;

  @HostBinding('class')
  private classes = 'flex flex-row gap-3 border-solid border-t border-navy-30 py-4 px-3';

  @Input()
  threadId: string;

  creation$ = new BehaviorSubject<boolean>(false);
  textControl = new UntypedFormControl(null, Validators.required);
  isFocused$: Observable<boolean>;
  currentUser$: Observable<UserClaims>;
  usersMap$: Observable<Map<string, UserModel>>;

  get localStorageKey(): string {
    return this.threadId ? `${commentDraftLocalStoragePrefix}-${this.threadId}` : null;
  }

  get draftMessage(): string {
    if (this.localStorageKey) {
      const commentDraft: string = this.windowHelper.getWindow().localStorage.getItem(this.localStorageKey);
      return commentDraft ? (JSON.parse(commentDraft) as CommentDraft)?.body : null;
    }
  }

  constructor(
    private commentingFacadeService: CommentingFacadeService,
    private commentPanelManagerService: CommentPanelManagerService,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    private windowHelper: WindowHelperService,
    private confirmationService: ConfirmationModalService
  ) {}

  ngOnInit(): void {
    this.usersMap$ = this.commentPanelManagerService.getUsersMap();
    this.isFocused$ = this.commentPanelManagerService
      .getReplyingThreadId()
      .pipe(map((threadId) => this.threadId === threadId));
    this.currentUser$ = this.authService.getUser();
    this.textControl.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cd.detectChanges());
  }

  async reply(): Promise<void> {
    if (await this.confirmationService.openAuditorMentionModal(this.textControl.value?.body)) {
      this.creation$.next(true);
      const user = await firstValueFrom(this.currentUser$);
      this.commentingFacadeService.createCommentAsync(
        this.threadId, this.textControl.value as CommentInputValue, user
      );
      await delayedPromise(500);
      this.input.reset();
      this.commentPanelManagerService.closeReplying();
      this.creation$.next(false);
    }
  }

  cancel(): void {
    this.commentPanelManagerService.closeReplying();
    this.input.reset();
  }

  inputGotFocus(): void {
    this.commentPanelManagerService.replyToThread(this.threadId);
  }

  inputLostFocus(): void {
    // TODO
  }

  buildTranslationKey(relativeKey: string): string {
    return `commenting.${relativeKey}`;
  }
}
